import { Box, Checkbox, Radio, IconButton } from "@primer/react";
import { ChevronRightIcon, ChevronUpIcon } from "@primer/octicons-react";
import { createContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import styles from "./DataGrid.module.css";
import Paging from "./Paging";
import Table from "./Table";
import Toolbar from "./toolbar/Toolbar";
import {
  dynamicSort,
  GetDataGridRowCell,
  handleFilter,
  handleSearchByDataField,
  IniColumns,
} from "./type/ColumnHelper";
import {
  ICondition,
  IDataGridColumn,
  IDataGridProps,
  ILogicOperator,
} from "./type/IDataGridProps";
import { useAutoTableHeight } from "./useAutoTableHeight";

export interface IDataGridContainerContext extends IDataGridProps {
  _pagedData: any[];
  _pageCount: number;
  _columns: IDataGridColumn[];
  _onColumnSettingChanged: (columns: IDataGridColumn[]) => void;
  _conditions: ICondition[];
  _setConditions: (conditions: ICondition[]) => void;
}

export const DataGridContainerContext = createContext(
  {} as IDataGridContainerContext,
);
const DataGrid = (props: IDataGridProps) => {
  const { height, dataSource } = props;

  const [logicOperator, setLogicOperator] = useState<ILogicOperator>("and");
  const [_columns, _setColumns] = useState<IDataGridColumn[]>([]);
  const [_paging, _setPaging] = useState(props.paging);
  const [_searchKeyDelayed, _setSearchKeyDelayed] = useState("");
  const [_sorting, _setSorting] = useState(props.sorting);
  const [_conditions, _setConditions] = useState<ICondition[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Map<string, string>>(new Map());
  const isGroupsInitialized = useRef(false);
  const _pageSize = useMemo(() => {
    return _paging?.pageSize ?? 20;
  }, [_paging?.pageSize]);
  const _pageIndex = useMemo(() => {
    return _paging?.pageIndex ?? 0;
  }, [_paging?.pageIndex]);

  const dataColumn = useMemo(() => {
    return GetDataGridRowCell(_columns);
  }, [_columns]);
  const stickyOffsets = useMemo(() => {
    const leftOffsets: Record<string, number> = {};
    let left = 0;
    const rightOffsets: Record<string, number> = {};
    let right = 0;
    dataColumn.forEach((col) => {
      if (col.fixed === "left") {
        leftOffsets[col.uuid ?? ""] = left;
        left += col.width ?? 100;
      }
    });

    [...dataColumn].reverse().forEach((col) => {
      if (col.fixed === "right") {
        rightOffsets[col.uuid ?? ""] = right;
        right += col.width ?? 100;
      }
    });

    return { leftOffsets, rightOffsets };
  }, [dataColumn]);
  const baseColumns = useMemo(() => {
    return IniColumns(props.columns);
  }, [props.columns]);
  const searchableColumns = useMemo(() => {
    return dataColumn.filter((x) => x.dataField || x.calculateCellValue || x.cellRender);
  }, [dataColumn]);
  useEffect(() => {
    _setPaging(pre => {
      if (pre) {
        return {
          ...pre,
          pageIndex: 0
        }
      }
      return pre;
    })
  }, [_conditions])

  // Toggle group expand/collapse
  const toggleGroup = useCallback((groupKey: string) => {
    setExpandedGroups(prev => {
      const newMap = new Map(prev);
      if (newMap.has(groupKey)) {
        newMap.delete(groupKey);
      } else {
        newMap.set(groupKey, groupKey);
      }
      if (props.grouping?.onGroupToggle) {
        props.grouping.onGroupToggle(groupKey, newMap.has(groupKey));
      }
      return newMap;
    });
  }, [props.grouping]);

  const filterdData: any[] = useMemo(() => {
    let resultData: any[] = [...dataSource];

    if (_searchKeyDelayed) {
      resultData = handleSearchByDataField(
        searchableColumns,
        dataSource,
        _searchKeyDelayed,
      );
    }
    if (_sorting?.enable !== false && _sorting?.mode && _sorting.field) {
      resultData = resultData.sort(dynamicSort(_sorting.field, _sorting.mode));
    }
    if (_conditions.length > 0) {
      resultData = handleFilter(_conditions, resultData, logicOperator, searchableColumns);
    }

    // Apply grouping if enabled
    if (props.grouping?.enable && props.grouping.groupField) {
      // Group data by groupField
      const groupedData: Record<string, any[]> = {};
      resultData.forEach((item: any) => {
        const groupKey = item[props.grouping!.groupField] || 'Khác';
        if (!groupedData[groupKey]) {
          groupedData[groupKey] = [];
        }
        groupedData[groupKey].push(item);
      });

      // Flatten data with group headers và chỉ hiển thị items khi expanded
      const flattened: any[] = [];
      Object.keys(groupedData).sort().forEach(groupKey => {
        // Add group header row
        flattened.push({
          id: `group_${groupKey}`,
          isGroupHeader: true,
          [props.grouping!.groupField]: groupKey,
          groupItems: groupedData[groupKey],
          isExpanded: expandedGroups.has(groupKey)
        });

        // Chỉ add group items nếu group được expand
        if (expandedGroups.has(groupKey)) {
          groupedData[groupKey].forEach(item => {
            flattened.push({
              ...item,
              isGroupHeader: false,
              parentGroup: groupKey
            });
          });
        }
      });

      resultData = flattened;
    }

    return resultData;
  }, [dataSource, _searchKeyDelayed, searchableColumns, _sorting, _conditions, props.grouping, expandedGroups]);

  // Tự động expand tất cả groups khi có dữ liệu và grouping được bật
  useEffect(() => {
    if (props.grouping?.enable && props.grouping.groupField && dataSource.length > 0) {
      // Lấy danh sách groups từ dataSource gốc (không phải filterdData để tránh vòng lặp)
      const uniqueGroups = new Set<string>();
      dataSource.forEach((item: any) => {
        // Bỏ qua group headers
        if (!item.isGroupHeader) {
          const groupKey = item[props.grouping!.groupField] || 'Khác';
          uniqueGroups.add(groupKey);
        }
      });

      // Chỉ khởi tạo hoặc thêm groups mới, không reset groups đã có
      if (uniqueGroups.size > 0) {
        setExpandedGroups(prev => {
          const newMap = new Map(prev);
          let hasNewGroups = false;

          uniqueGroups.forEach(groupKey => {
            // Thêm group mới nếu chưa có và defaultExpanded !== false
            if (!newMap.has(groupKey) && props.grouping!.defaultExpanded !== false) {
              newMap.set(groupKey, groupKey);
              hasNewGroups = true;
            }
          });

          // Chỉ đánh dấu đã khởi tạo nếu có thêm groups mới
          if (hasNewGroups && !isGroupsInitialized.current) {
            isGroupsInitialized.current = true;
          }

          return newMap;
        });
      }
    }

    // Reset flag khi grouping bị tắt
    if (!props.grouping?.enable) {
      isGroupsInitialized.current = false;
      setExpandedGroups(new Map());
    }
  }, [props.grouping?.enable, props.grouping?.groupField, props.grouping?.defaultExpanded, dataSource]);

  const _pageCount = useMemo(() => {
    if (_pageSize <= 0) return filterdData.length;
    const c = Math.floor(filterdData.length / _pageSize);
    if (c * _pageSize < filterdData.length) return c + 1;
    return c;
  }, [filterdData.length, _pageSize]);

  const _pagedData = useMemo(() => {
    let result: any[] = [];
    if (_paging?.enable === true) {
      const data = filterdData.slice(
        _pageSize * _pageIndex,
        _pageSize * _pageIndex + _pageSize,
      );
      result = data;
    } else {
      result = filterdData;
    }
    return result;
  }, [filterdData, _pageIndex, _pageSize, _paging?.enable]);
  const filterdDataKeys = useMemo(() => {
    // Loại bỏ group headers khỏi keys
    return filterdData
      .filter((x) => !x.isGroupHeader)
      .map((x) => x[props.keyExpr ?? "id"]);
  }, [filterdData, props.keyExpr]);

  const selectableDataKeys = useMemo(() => {
    return filterdData
      .filter((x) => !x.isGroupHeader && !props.selection?.isRowSelectionDisabled?.(x))
      .map((x) => x[props.keyExpr ?? "id"]);
  }, [filterdData, props.keyExpr, props.selection?.isRowSelectionDisabled]);

  const selectedRowKeys = useMemo(() => {
    return props.selection?.selectedRowKeys ?? [];
  }, [props.selection]);

  // Handle group selection - chọn/bỏ chọn tất cả items trong group
  const handleGroupSelection = useCallback((groupKey: string, isSelected: boolean) => {
    if (!props.selection || !props.grouping?.groupField) return;

    // Lấy tất cả items trong group từ dataSource gốc (không phụ thuộc vào expand/collapse)
    const groupItems = dataSource.filter((item: any) => {
      if (item.isGroupHeader || props.selection?.isRowSelectionDisabled?.(item)) return false;
      const itemGroupKey = item[props.grouping!.groupField] || 'Khác';
      return itemGroupKey === groupKey;
    });

    const groupItemIds = groupItems.map(item => item[props.keyExpr ?? "id"]);
    const currentSelected = props.selection.selectedRowKeys ?? [];

    if (isSelected) {
      // Thêm tất cả items trong group vào selection
      const newSelection = [...currentSelected];
      groupItemIds.forEach(id => {
        if (!newSelection.includes(id)) {
          newSelection.push(id);
        }
      });
      props.selection.onSelectionChanged(newSelection);
    } else {
      // Loại bỏ tất cả items trong group khỏi selection
      const newSelection = currentSelected.filter(id => !groupItemIds.includes(id));
      props.selection.onSelectionChanged(newSelection);
    }
  }, [props.selection, props.grouping, dataSource, props.keyExpr]);

  const isSelectedAll = useMemo(() => {
    return (
      selectableDataKeys.length > 0 &&
      selectableDataKeys.every((x) => selectedRowKeys.includes(x))
    );
  }, [selectedRowKeys, selectableDataKeys]);

  const selectionColumn: IDataGridColumn | null = useMemo(() => {
    if (!props.selection) return null;
    return {
      id: "selection",
      width: 40,
      align: "center",
      caption: "#",
      fixed: "left",
      isVisible: true,
      uuid: uuidv4(),
      headerRender: () => {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {props.selection?.mode === "multiple" && (
              <Checkbox
                checked={isSelectedAll}
                onChange={(e) => {
                  if (e.target.checked) {
                    // Combine existing selection with new selectable keys
                    const newSelection = Array.from(new Set([...selectedRowKeys, ...selectableDataKeys]));
                    props.selection?.onSelectionChanged(newSelection);
                  } else {
                    // Remove current selectable keys from selection
                    const newSelection = selectedRowKeys.filter(x => !selectableDataKeys.includes(x));
                    props.selection?.onSelectionChanged(newSelection);
                  }
                }}
              />
            )}
          </Box>
        );
      },
      cellRender: (data: any) => {
        // Không hiển thị selection cho group headers
        if (data.isGroupHeader) {
          return <span></span>;
        }
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {props.selection?.mode === "multiple" && (
              <Checkbox
                checked={selectedRowKeys.includes(data[props.keyExpr ?? "id"])}
                disabled={props.selection?.isRowSelectionDisabled?.(data)}
                onChange={(e) => {
                  if (e.target.checked) {
                    props.selection?.onSelectionChanged([
                      ...selectedRowKeys,
                      data[props.keyExpr ?? "id"],
                    ]);
                  } else {
                    props.selection?.onSelectionChanged(
                      selectedRowKeys.filter(
                        (x) => x !== data[props.keyExpr ?? "id"],
                      ),
                    );
                  }
                }}
              />
            )}
            {props.selection?.mode === "single" && (
              <Radio
                name={data[props.keyExpr ?? "id"]}
                value={data[props.keyExpr ?? "id"]}
                aria-label="Select row"
                checked={selectedRowKeys.includes(data[props.keyExpr ?? "id"])}
                disabled={props.selection?.isRowSelectionDisabled?.(data)}
                onChange={(e) => {
                  if (e.target.checked) {
                    props.selection?.onSelectionChanged([
                      data[props.keyExpr ?? "id"],
                    ]);
                  } else {
                    props.selection?.onSelectionChanged([]);
                  }
                }}
              />
            )}
          </Box>
        );
      },
    };
  }, [props.keyExpr, isSelectedAll, selectedRowKeys, filterdDataKeys]);
  const idxColumn: IDataGridColumn | null = useMemo(() => {
    if (props.isShowRowIdx === false) return null;
    return {
      id: "col_idx",
      width: 40,
      align: "center",
      caption: "#",
      fixed: "left",
      isVisible: true,
      uuid: uuidv4(),
    };
  }, []);

  const finalColumns = useMemo(() => {
    let cols: IDataGridColumn[] = [];
    if (idxColumn) cols.push(idxColumn);
    if (selectionColumn) cols.push(selectionColumn);

    // Wrap cột đầu tiên có dataField để hiển thị group header nếu có grouping
    const wrappedColumns = [...baseColumns];
    if (props.grouping?.enable && wrappedColumns.length > 0) {
      const firstDataFieldColumnIndex = wrappedColumns.findIndex(col => col.dataField);
      if (firstDataFieldColumnIndex >= 0) {
        const firstColumn = wrappedColumns[firstDataFieldColumnIndex];
        const originalCellRender = firstColumn.cellRender;
        wrappedColumns[firstDataFieldColumnIndex] = {
          ...firstColumn,
          cellRender: (data: any) => {
            // Nếu là group header, hiển thị group header
            if (data.isGroupHeader && props.grouping?.groupField) {
              const groupKey = data[props.grouping.groupField] || 'Khác';
              const isExpanded = expandedGroups.has(groupKey);

              // Tính toán selection state cho group
              let isAllSelected = false;
              let isPartialSelected = false;
              if (props.selection?.mode === "multiple") {
                const groupItems = dataSource.filter((item: any) => {
                  if (item.isGroupHeader) return false;
                  const itemGroupKey = item[props.grouping!.groupField] || 'Khác';
                  return itemGroupKey === groupKey;
                });
                const groupItemIds = groupItems.map(item => item[props.keyExpr ?? "id"]);
                const selectedCount = groupItemIds.filter(id => selectedRowKeys.includes(id)).length;
                isAllSelected = groupItemIds.length > 0 && selectedCount === groupItemIds.length;
                isPartialSelected = selectedCount > 0 && selectedCount < groupItemIds.length;
              }

              if (props.grouping.groupHeaderRender) {
                return props.grouping.groupHeaderRender(groupKey, isExpanded, () => toggleGroup(groupKey));
              }

              // Default group header render với checkbox
              return (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <IconButton
                    icon={!isExpanded ? ChevronRightIcon : ChevronUpIcon}
                    aria-label=''
                    variant='invisible'
                    size='small'
                    onClick={() => toggleGroup(groupKey)}
                  />
                  {props.selection?.mode === "multiple" && (
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={isPartialSelected && !isAllSelected}
                      onChange={(e) => handleGroupSelection(groupKey, e.target.checked)}
                      sx={{ ml: 1 }}
                    />
                  )}
                  <Box sx={{ fontSize: '13px', fontWeight: 'bold', marginLeft: '8px', color: '#0969da' }}>
                    Nhóm: {groupKey}
                  </Box>
                </Box>
              );
            }

            // Nếu là item trong group, thêm indentation
            if (!data.isGroupHeader && data.parentGroup && originalCellRender) {
              const level = 1;
              const originalContent = originalCellRender(data);
              return (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box sx={{ width: `${level * 30}px` }}>&nbsp;</Box>
                  <Box sx={{ marginLeft: '8px' }}>{originalContent}</Box>
                </Box>
              );
            }

            // Render bình thường
            return originalCellRender ? originalCellRender(data) : <span>{data[firstColumn.dataField || ''] || ''}</span>;
          }
        };
      }
    }

    cols.push(...wrappedColumns);
    return cols;
  }, [baseColumns, selectionColumn, idxColumn, props.grouping, expandedGroups, toggleGroup, handleGroupSelection, selectedRowKeys, dataSource, props.selection, props.keyExpr]);

  useEffect(() => {
    _setColumns(finalColumns);
  }, [finalColumns]);
  const _onColumnSettingChanged = (columns: IDataGridColumn[]) => {
    _setColumns(columns);
  };

  const contextValue: IDataGridContainerContext = {
    ...props,
    _pagedData,
    _pageCount,
    _columns: _columns,
    _onColumnSettingChanged,
    _conditions,
    _setConditions,
  };

  const { containerRef, toolbarRef, tableRef, pagingRef, tableHeight } =
    useAutoTableHeight({ debounceMs: 100 });

  return (
    <DataGridContainerContext.Provider value={contextValue}>
      <Box
        sx={{
          height: height,
          overflow: "auto",
          // border: "1px solid #ccc",
        }}
        className={styles.container}
        ref={containerRef}
      >
        <Box ref={toolbarRef} sx={{ pb: 1 }}>
          <Toolbar
            onSearchKeyChanged={_setSearchKeyDelayed}
            filterdData={filterdData}
            dataColumns={dataColumn}
          />
          {props.subToolbarElement && <>{props.subToolbarElement}</>}
        </Box>
        <Box ref={tableRef}>
          <Table
            conditions={_conditions}
            dataSource={props.dataSource}
            dataColumn={dataColumn}
            stickyOffsets={stickyOffsets}
            height={props.height ? tableHeight : undefined}
            sorting={_sorting}
            onSortingChanged={(value) => {
              if (_sorting?.enable !== false) {
                _setSorting(value);
              }
            }}
            isLoading={props.isLoading}
            focusedCell={props.focusedCell}
            mainColor={"#1a7f37"}
            totalSummary={props.totalSummary}
            filterdData={filterdData}
          />
        </Box>
        <Box ref={pagingRef}>
          {props.paging?.enable === true && _pageCount > 0 && (
            <Paging
              pageSizeItems={props.paging.pageSizeItems}
              pageCount={_pageCount}
              pageIndex={_pageIndex}
              pageSize={_pageSize}
              totalCount={filterdData.length}
              onPagingChanged={(pageIndex, pageSize) => {
                debugger
                _setPaging({
                  ..._paging,
                  pageIndex: pageIndex,
                  pageSize: pageSize,
                } as any);
              }}
            />
          )}
        </Box>
      </Box>
    </DataGridContainerContext.Provider>
  );
};

export default DataGrid;
